// Small pieces of real product UI for marketing cards. Built from the same
// components and tokens as the app, so they never go stale. All data is example data.
import { StatusBadge } from '@/components/status-badge'
import { EDGE_LOCATION_COUNT, MONITOR_LOCATIONS } from '@/lib/check-locations'

function Dot({ tone }: { tone: 'up' | 'down' }) {
  return <span className={`size-2 shrink-0 rounded-full ${tone === 'up' ? 'bg-status-up' : 'bg-status-down'}`} />
}

/** One location of 24 fails, the rest are up: no alert. */
export function RegionConsensusPiece() {
  const rows = MONITOR_LOCATIONS.consensusPicture.rows
  return (
    <div className="flex h-full flex-col justify-center gap-2 p-5 text-sm" role="img" aria-label={MONITOR_LOCATIONS.consensusPicture.label}>
      {rows.map((r) => (
        <div key={r.name} className="flex items-center gap-2.5">
          <Dot tone={r.up ? 'up' : 'down'} />
          <span className="flex-1 text-[13px] text-foreground">{r.name}</span>
          <span className="num text-xs text-muted-foreground">{r.ms}</span>
          <StatusBadge kind={r.up ? 'up' : 'down'} />
        </div>
      ))}
      <div className="mt-1 flex items-center justify-between border-t border-border pt-2.5">
        <span className="text-body">Consensus · {EDGE_LOCATION_COUNT - 1} of {EDGE_LOCATION_COUNT} up</span>
        <span className="rounded-md bg-status-up-soft px-2 py-0.5 text-xs font-medium text-status-up">No alert</span>
      </div>
    </div>
  )
}

/** Thirty days of uptime, one degraded day. */
export function UptimeBarsPiece() {
  return (
    <div className="flex h-full flex-col justify-center gap-2.5 p-5" role="img" aria-label="Example: 30 days of uptime with one degraded day">
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-mono text-[13px] text-foreground">api.example.com</span>
        <span className="num text-foreground">99.98%</span>
      </div>
      <div className="flex h-9 gap-[3px]">
        {Array.from({ length: 30 }, (_, i) => (
          <span key={i} className={`flex-1 rounded-[2px] ${i === 21 ? 'bg-status-degraded' : 'bg-status-up'}`} />
        ))}
      </div>
      <div className="flex justify-between num text-xs text-muted-foreground">
        <span>30 days</span>
        <span>p95 221 ms</span>
      </div>
    </div>
  )
}

/** An alert message as a chat channel shows it. */
export function ChatAlertPiece() {
  return (
    <div className="flex h-full items-center p-5" role="img" aria-label="Example alert message in a chat channel: checkout-flow is down, failed at step 4">
      <div className="w-full rounded-lg border border-border bg-popover p-3.5 text-sm">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-foreground">Uptrack</span>
          <span className="num text-xs text-muted-foreground">#on-call · 14:02</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-body">
          <StatusBadge kind="down" />
          <span className="font-medium text-foreground">checkout-flow</span>
        </div>
        <p className="mt-1.5 text-body">Failed at step 4, “Click Pay now”. Confirmed in 3 of 3 regions.</p>
      </div>
    </div>
  )
}

/** Floating alert that overlaps a product frame. */
export function AlertToast({ className = '' }: { className?: string }) {
  return (
    <div className={`w-[270px] rounded-lg border border-border bg-popover p-3.5 text-[13px] leading-snug text-body shadow-raised ${className}`}>
      <p className="font-semibold text-foreground">Alert sent to #on-call</p>
      <p className="mt-1">checkout-flow failed at step 4 in EU, US and India.</p>
      <p className="mt-1.5 num text-xs text-muted-foreground">14:02 · confirmed after 3 checks</p>
    </div>
  )
}

/** Dark stage that holds a live product piece on a card. Product UI is always dark. */
export function PieceStage({ children }: { children: React.ReactNode }) {
  return <div className="dark h-[176px] border-b border-border bg-background text-foreground">{children}</div>
}

/** A monitor row: name, detail and status. Names are hosts or paths, so they stay mono unless `plainName`. */
function Row({ name, detail, kind, plainName = false }: { name: string; detail: string; kind: 'up' | 'down' | 'degraded'; plainName?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className={`flex-1 truncate text-[13px] text-foreground ${plainName ? '' : 'font-mono'}`}>{name}</span>
      <span className="num text-xs text-muted-foreground">{detail}</span>
      <StatusBadge kind={kind} />
    </div>
  )
}

function PieceFooter({ left, right }: { left: string; right: string }) {
  return (
    <div className="mt-1 flex items-center justify-between border-t border-border pt-2.5 text-sm">
      <span className="text-body">{left}</span>
      <span className="num text-xs text-muted-foreground">{right}</span>
    </div>
  )
}

/** SSL certificates with days left; one expires soon. */
export function SslExpiryPiece() {
  return (
    <div className="flex h-full flex-col justify-center gap-2 p-5 text-sm" role="img" aria-label="Example: three SSL certificates; shop.example.com expires in 6 days">
      <Row name="example.com" detail="81 days" kind="up" />
      <Row name="api.example.com" detail="52 days" kind="up" />
      <Row name="shop.example.com" detail="6 days" kind="degraded" />
      <PieceFooter left="Alerts 14, 30 or 60 days ahead" right="issuer + chain checked" />
    </div>
  )
}

/** A DNS record that changed. */
export function DnsRecordPiece() {
  return (
    <div className="flex h-full flex-col justify-center gap-2 p-5 text-sm" role="img" aria-label="Example: the A record of example.com changed from 203.0.113.10 to 198.51.100.7">
      <Row name="MX example.com" detail="mx1.example.net" kind="up" />
      <Row name="TXT example.com" detail="v=spf1 …" kind="up" />
      <Row name="A example.com" detail="198.51.100.7" kind="down" />
      <PieceFooter left="Changed from 203.0.113.10" right="09:41 UTC" />
    </div>
  )
}

/** Cron jobs checking in; one is late. */
export function HeartbeatPiece() {
  return (
    <div className="flex h-full flex-col justify-center gap-2 p-5 text-sm" role="img" aria-label="Example: two cron jobs checked in on time; nightly-backup is 12 minutes late">
      <Row name="invoice-sync" detail="every 15 min" kind="up" />
      <Row name="send-digest" detail="daily 08:00" kind="up" />
      <Row name="nightly-backup" detail="12 min late" kind="down" />
      <PieceFooter left="Grace period 10 min" right="last ping 02:00" />
    </div>
  )
}

/** TCP ports with connect times. */
export function TcpPortPiece() {
  return (
    <div className="flex h-full flex-col justify-center gap-2 p-5 text-sm" role="img" aria-label="Example: Postgres and Redis ports accept connections; SMTP port 587 refuses">
      <Row name="db.example.com:5432" detail="8 ms" kind="up" />
      <Row name="cache.example.com:6379" detail="3 ms" kind="up" />
      <Row name="mail.example.com:587" detail="refused" kind="down" />
      <PieceFooter left="Checked every 30 s" right="2 regions" />
    </div>
  )
}

/** Ping latency and packet loss per region. */
export function PingLatencyPiece() {
  return (
    <div className="flex h-full flex-col justify-center gap-2 p-5 text-sm" role="img" aria-label="Example: ping latency from two regions, 0 percent packet loss">
      <Row name="EU · Nuremberg" detail="18 ms" kind="up" plainName />
      <Row name="India · Hyderabad" detail="141 ms" kind="up" plainName />
      <PieceFooter left="Packet loss 0%" right="vps.example.com" />
    </div>
  )
}

/** A keyword check that failed because the text is missing. */
export function KeywordPiece() {
  return (
    <div className="flex h-full flex-col justify-center gap-2 p-5 text-sm" role="img" aria-label="Example: the page returned 200 but the text Add to cart is missing, so the check fails">
      <Row name="/pricing" detail='"See plans"' kind="up" />
      <Row name="/status" detail='"All systems"' kind="up" />
      <Row name="/product/42" detail='"Add to cart"' kind="down" />
      <PieceFooter left="HTTP 200, keyword missing" right="14:02" />
    </div>
  )
}

/** A public status page header with components. */
export function StatusPagePiece() {
  return (
    <div className="flex h-full flex-col justify-center gap-2 p-5 text-sm" role="img" aria-label="Example status page: API degraded, website and checkout operational">
      <div className="mb-1 rounded-md border border-status-degraded bg-status-degraded-soft px-3 py-2 text-status-degraded">
        Some systems are degraded
      </div>
      <Row name="Website" detail="99.99%" kind="up" />
      <Row name="API" detail="99.81%" kind="degraded" />
      <Row name="Checkout" detail="100%" kind="up" />
    </div>
  )
}
